/**
 * Per-client dashboard theming (#959) — colour maths and CSS derivation.
 *
 * A client's brand colours are already stored on `brand_bible.theme_config`
 * and already editable from the Brand Bible operator UI. This module turns
 * whatever is stored there into a stylesheet that re-points the *primary*
 * design tokens (`--color-coral*`, shadcn `--primary`/`--ring`/`--accent`) at
 * the client's colour, so the dashboard reads as theirs.
 *
 * Three properties matter more than the colour itself:
 *
 *   1. **Fail open.** Every step returns `null` rather than a guess. A tenant
 *      with no usable colour gets no stylesheet at all and looks exactly as it
 *      does today.
 *   2. **Contrast is enforced, not assumed.** Real stored values include a
 *      near-white cream and a vivid yellow that are invisible on a white card.
 *      The seed is treated as a hue/saturation *intent* and its lightness is
 *      moved until it clears the contrast floor against the surface it will be
 *      painted on. Light and dark surfaces get separately corrected values.
 *   3. **Status colours are never touched.** Teal (in-progress), destructive,
 *      amber and the chart ramp carry learned meaning. Repainting them with a
 *      client's brand would make a red-branded client's dashboard look like
 *      everything is failing.
 *
 *   4. **The teal register is guarded by measurement, not trust.** After the
 *      accent is derived (light AND dark variants), it is measured against
 *      the teal status family in OKLab; inside ΔE 0.1 the whole theme is
 *      refused and the client gets the house palette (fail open, property 1).
 *      A teal-looking "primary action" would read as a status, not a brand.
 *
 *   5. **Amber is protected by construction, not by metric.** The arithmetic
 *      kills every amber metric: ΔE(coral↔amber) = 0.06 < ΔE(coral↔coral-hover)
 *      = 0.08 — amber sits *inside* the warm-brand cluster, so any distance
 *      threshold that caught amber impersonators would also eat the house
 *      fallback. It needs none: amber status only ever renders as the
 *      light-background pill composition (`bg-amber-light text-amber-dark`,
 *      with words and a glyph), which no solid brand-accent surface can
 *      impersonate.
 *
 * Token roles — why "brand = action" is an intentional conflation:
 *
 *   - **coral / --primary / --ring / --sidebar-primary — the REPAINTABLE
 *     action-and-brand register.** Primary buttons ARE the brand; this module
 *     exists to make them the client's colour. Brand and action sharing one
 *     hue is deliberate: one strong, recognisable signal carries both.
 *   - **teal, amber, destructive, ink, the chart ramp — the STABLE
 *     signifiers.** They never rebrand, so the meanings operators have
 *     learned (connected, needs-action, danger, text, data) never move.
 */

/* -------------------------------------------------------------------------- */
/* Colour primitives                                                          */
/* -------------------------------------------------------------------------- */

export interface Hsl {
	/** Hue in degrees, 0–360. */
	h: number;
	/** Saturation, 0–1. */
	s: number;
	/** Lightness, 0–1. */
	l: number;
}

interface Rgb {
	r: number;
	g: number;
	b: number;
}

/**
 * Parse a hex colour.
 *
 * The leading `#` is **required**. Brand-bible colour fields are free text and
 * have held things like `"true"`, `"black and white"` and `"Professional /
 * Corporate"`; without the `#` guard an innocent English word made of hex
 * digits (`decade`, `faded`) would silently parse as a colour.
 */
export function parseHex(value: string): Rgb | null {
	const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
	if (!match) return null;

	let digits = match[1];
	if (digits.length === 3) {
		digits = digits
			.split("")
			.map((d) => d + d)
			.join("");
	}

	return {
		r: parseInt(digits.slice(0, 2), 16),
		g: parseInt(digits.slice(2, 4), 16),
		b: parseInt(digits.slice(4, 6), 16),
	};
}

function clamp01(value: number): number {
	return Math.min(1, Math.max(0, value));
}

export function hexToHsl(hex: string): Hsl | null {
	const rgb = parseHex(hex);
	if (!rgb) return null;

	const r = rgb.r / 255;
	const g = rgb.g / 255;
	const b = rgb.b / 255;
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const l = (max + min) / 2;
	const delta = max - min;

	if (delta === 0) return { h: 0, s: 0, l };

	const s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
	let h: number;
	if (max === r) h = ((g - b) / delta) % 6;
	else if (max === g) h = (b - r) / delta + 2;
	else h = (r - g) / delta + 4;

	h = h * 60;
	if (h < 0) h += 360;

	return { h, s, l };
}

export function hslToHex({ h, s, l }: Hsl): string {
	const c = (1 - Math.abs(2 * l - 1)) * s;
	const hp = (((h % 360) + 360) % 360) / 60;
	const x = c * (1 - Math.abs((hp % 2) - 1));
	const [r1, g1, b1] =
		hp < 1
			? [c, x, 0]
			: hp < 2
				? [x, c, 0]
				: hp < 3
					? [0, c, x]
					: hp < 4
						? [0, x, c]
						: hp < 5
							? [x, 0, c]
							: [c, 0, x];

	const m = l - c / 2;
	const toByte = (v: number) =>
		Math.round(clamp01(v + m) * 255)
			.toString(16)
			.padStart(2, "0");

	return `#${toByte(r1)}${toByte(g1)}${toByte(b1)}`.toUpperCase();
}

/** WCAG 2.x relative luminance. */
export function relativeLuminance(hex: string): number {
	const rgb = parseHex(hex);
	if (!rgb) return 0;

	const channel = (byte: number) => {
		const v = byte / 255;
		return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
	};

	return (
		0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b)
	);
}

/** WCAG 2.x contrast ratio between two hex colours (1–21, symmetric). */
export function contrastRatio(a: string, b: string): number {
	const la = relativeLuminance(a);
	const lb = relativeLuminance(b);
	const lighter = Math.max(la, lb);
	const darker = Math.min(la, lb);
	return (lighter + 0.05) / (darker + 0.05);
}

/**
 * How much usable colour a value carries — chroma in the HSL cylinder.
 *
 * Saturation alone is misleading: `#F1EDE3` reports ~28% saturation but is a
 * near-white cream with no visible hue. Weighting by distance from the
 * black/white poles gives a number that actually tracks "is this a colour".
 */
export function chroma(hex: string): number {
	const hsl = hexToHsl(hex);
	if (!hsl) return 0;
	return hsl.s * (1 - Math.abs(2 * hsl.l - 1));
}

/* -------------------------------------------------------------------------- */
/* Surfaces and thresholds                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Minimum contrast a brand accent must clear against the surface behind it.
 *
 * 3.0:1 is the WCAG 2.2 §1.4.11 floor for UI components and graphical objects,
 * and it is also roughly where the house coral already sits (`#D97757` on
 * white is 3.12:1). Demanding 4.5:1 would push every client's colour
 * *darker than the house palette*, which reads as a bug rather than as their
 * brand; accepting less lets a pale yellow vanish into the card.
 */
export const MIN_ACCENT_CONTRAST = 3.0;

/** Minimum contrast for text painted on the pale accent tint. */
export const MIN_ON_TINT_CONTRAST = 4.5;

/**
 * A candidate colour below this chroma carries no usable hue (black, white,
 * greys, the near-white creams some brand bibles store). Such a tenant falls
 * back to the house palette rather than getting a grey dashboard.
 */
export const MIN_SEED_CHROMA = 0.12;

/* -------------------------------------------------------------------------- */
/* Perceptual distance (OKLab) and the teal-register guard                    */
/* -------------------------------------------------------------------------- */

interface Oklab {
	L: number;
	a: number;
	b: number;
}

function srgbChannelToLinear(channel: number): number {
	const c = channel / 255;
	return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** sRGB → OKLab (Björn Ottosson's reference transform). */
export function rgbToOklab(rgb: Rgb): Oklab {
	const r = srgbChannelToLinear(rgb.r);
	const g = srgbChannelToLinear(rgb.g);
	const b = srgbChannelToLinear(rgb.b);

	const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
	const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
	const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

	const lRoot = Math.cbrt(l);
	const mRoot = Math.cbrt(m);
	const sRoot = Math.cbrt(s);

	return {
		L: 0.2104542553 * lRoot + 0.793617785 * mRoot - 0.0040720468 * sRoot,
		a: 1.9779984951 * lRoot - 2.428592205 * mRoot + 0.4505937099 * sRoot,
		b: 0.0259040371 * lRoot + 0.7827717662 * mRoot - 0.808675766 * sRoot,
	};
}

/** Euclidean distance in OKLab — the perceptual ΔE the teal guard uses. */
export function deltaEOklab(first: Oklab, second: Oklab): number {
	const dL = first.L - second.L;
	const da = first.a - second.a;
	const db = first.b - second.b;
	return Math.sqrt(dL * dL + da * da + db * db);
}

/**
 * The rendered teal register (`--color-teal`, `--color-teal-dark` from
 * index.css) — the only status family a distance metric can protect without
 * eating the house fallback. Amber is protected by construction instead; see
 * the module header for the arithmetic and the token-role table.
 */
const STATUS_TEAL_OKLAB: readonly Oklab[] = ["#4DD4C4", "#14B8A6"].map((hex) => {
	const rgb = parseHex(hex);
	if (!rgb) throw new Error(`status teal token is not a colour: ${hex}`);
	return rgbToOklab(rgb);
});

/**
 * The confusability band for the teal guard. 0.1 in OKLab separates
 * "same family at a glance" from "merely related": teal vs teal-dark is
 * ~0.13 apart (distinct siblings), the mock's forest green is ~0.36 from
 * teal, and coral vs teal is ~0.27 — so teal-adjacent brand accents collide
 * while honest greens, blues, and the whole warm cluster pass.
 */
export const TEAL_COLLISION_DELTA_E = 0.1;

/**
 * True when `hex` is perceptually confusable with the teal status register
 * (ΔE below TEAL_COLLISION_DELTA_E from any rendered teal value).
 */
export function collidesWithStatusTeal(hex: string): boolean {
	const rgb = parseHex(hex);
	if (!rgb) return false;
	const lab = rgbToOklab(rgb);
	return STATUS_TEAL_OKLAB.some(
		(teal) => deltaEOklab(lab, teal) < TEAL_COLLISION_DELTA_E,
	);
}

/** Lightest surface a light-mode accent is painted on (`--card`). */
const LIGHT_SURFACE = "#FFFFFF";

/** Darkest-mode card surface, `--card: 185 20% 10%` from index.css. */
export const DARK_SURFACE = "#141F20";

/* -------------------------------------------------------------------------- */
/* Seed selection                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The colour slots a brand bible may carry, in the order we trust them.
 *
 * `theme_config.colors` is free-form and heterogeneous across tenants — some
 * rows have `{primary, secondary, accent, text, background}`, some only
 * `{primary}`, some have junk keys. `text` and `background` are deliberately
 * excluded: they describe the client's *website* body copy and page
 * background, not an accent, and are almost always near-black/near-white.
 */
const SEED_KEYS = ["primary", "secondary", "accent"] as const;

/**
 * Pull every plausible hex out of a brand bible's colour config, in priority
 * order. Values may be a single hex, a comma-joined list (the Brand Bible UI
 * stores multi-swatch fields as `"#003388,#003366"`), or an array.
 */
export function extractBrandColors(
	colors: Record<string, unknown> | null | undefined,
): string[] {
	if (!colors || typeof colors !== "object") return [];

	const out: string[] = [];
	const push = (raw: unknown) => {
		if (Array.isArray(raw)) {
			raw.forEach(push);
			return;
		}
		if (typeof raw !== "string") return;
		for (const token of raw.split(/[,\s]+/)) {
			const hex = token.trim();
			if (parseHex(hex)) out.push(hex.toUpperCase());
		}
	};

	for (const key of SEED_KEYS) push(colors[key]);
	push(colors.additional);

	return out;
}

/**
 * Choose the colour the dashboard should be themed with.
 *
 * The client's declared `primary` wins whenever it is actually a colour. The
 * fall-through exists for the very common black-and-X brand: a client whose
 * primary is `#000000` and whose accent is `#CC0000` means *red*, and a
 * greyscale dashboard would be a worse answer than either their red or the
 * house coral.
 */
export function pickBrandSeed(
	colors: Record<string, unknown> | null | undefined,
): string | null {
	for (const hex of extractBrandColors(colors)) {
		if (chroma(hex) >= MIN_SEED_CHROMA) return hex;
	}
	return null;
}

/* -------------------------------------------------------------------------- */
/* Contrast correction                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Walk a colour's lightness until it clears `minRatio` against `surface`,
 * keeping hue and saturation — the parts a client recognises as "their" colour.
 *
 * Direction is inferred from the surface: darken on light surfaces, lighten on
 * dark ones. Stops at the near-black/near-white poles, so an impossible demand
 * degrades to the most legible version of the colour instead of looping.
 */
export function ensureContrast(
	hex: string,
	surface: string,
	minRatio: number,
): string {
	const hsl = hexToHsl(hex);
	if (!hsl) return hex;

	const darken = relativeLuminance(surface) > 0.5;
	const step = darken ? -0.01 : 0.01;
	const limit = darken ? 0.08 : 0.95;

	let current = { ...hsl };
	let candidate = hslToHex(current);

	for (let i = 0; i < 100; i += 1) {
		if (contrastRatio(candidate, surface) >= minRatio) return candidate;

		const nextL = current.l + step;
		if (darken ? nextL < limit : nextL > limit) break;

		current = { ...current, l: nextL };
		candidate = hslToHex(current);
	}

	return candidate;
}

/* -------------------------------------------------------------------------- */
/* Theme derivation                                                           */
/* -------------------------------------------------------------------------- */

export interface BrandThemeVars {
	/**
	 * Custom properties that are the same in both modes, because index.css has
	 * no `.dark` counterpart for them.
	 */
	shared: Record<string, string>;
	/**
	 * Light-mode values for properties that *do* have a `.dark` counterpart in
	 * index.css. Emitted under `:root:not(.dark)`.
	 */
	light: Record<string, string>;
	/** Dark-mode values, emitted under `.dark`. */
	dark: Record<string, string>;
}

export interface BrandTheme extends BrandThemeVars {
	/** The colour actually stored by the client, before contrast correction. */
	seed: string;
	/** The light-mode accent after contrast correction. */
	accent: string;
	/** The dark-mode accent after contrast correction (against DARK_SURFACE). */
	darkAccent: string;
	/** True when contrast correction had to move the seed's lightness. */
	adjusted: boolean;
}

/** Format an HSL triple the way shadcn's CSS variables expect: `H S% L%`. */
function shadcnTriple(hex: string): string {
	const hsl = hexToHsl(hex);
	if (!hsl) return "0 0% 0%";
	return `${Math.round(hsl.h)} ${Math.round(hsl.s * 100)}% ${Math.round(hsl.l * 100)}%`;
}

/**
 * Build the full token override set from a single brand seed colour.
 *
 * Only the *primary/accent* family is derived. `--color-teal*`, `--destructive`,
 * the status colours and the chart ramp keep their house values on purpose —
 * see the module header.
 */
export function deriveBrandTheme(seed: string): BrandTheme | null {
	const hsl = hexToHsl(seed);
	if (!hsl) return null;

	// --- light mode -------------------------------------------------------
	const accent = ensureContrast(seed, LIGHT_SURFACE, MIN_ACCENT_CONTRAST);
	// Teal-register collision guard (module header, property 4): a derived
	// accent confusable with the teal status family refuses the WHOLE theme —
	// null here means the fail-open house path, never a different seed.
	if (collidesWithStatusTeal(accent)) return null;
	const accentHsl = hexToHsl(accent) ?? hsl;

	const hover = hslToHex({ ...accentHsl, l: clamp01(accentHsl.l - 0.08) });
	// Floored at 16% lightness: an already-dark seed such as `#015951` would
	// otherwise clamp to pure black and throw the brand hue away entirely.
	const deep = hslToHex({
		...accentHsl,
		l: Math.max(clamp01(accentHsl.l - 0.18), 0.16),
	});

	// A pale tint of the same hue, used as `--color-coral-light` / `--accent`.
	// Saturation is floored so a low-saturation brand still tints visibly, and
	// capped so a vivid brand does not produce a glaring wash.
	const tint = hslToHex({
		h: accentHsl.h,
		s: Math.min(Math.max(accentHsl.s, 0.35), 0.9),
		l: 0.96,
	});
	// Text sitting on that tint has to be readable against the tint, not white.
	const onTint = ensureContrast(deep, tint, MIN_ON_TINT_CONTRAST);

	// --- dark mode --------------------------------------------------------
	// The house dark tokens lighten coral (`15 63% 60%` → `15 55% 72%`) so it
	// reads against a near-black card. A brand colour gets the same treatment,
	// driven by measured contrast rather than a fixed offset.
	const darkAccent = ensureContrast(seed, DARK_SURFACE, MIN_ACCENT_CONTRAST);
	// The dark variant gets the same guard: contrast correction can walk a
	// dark teal seed straight into the register on the dark surface.
	if (collidesWithStatusTeal(darkAccent)) return null;
	const darkAccentHsl = hexToHsl(darkAccent) ?? hsl;
	const darkHover = hslToHex({
		...darkAccentHsl,
		l: clamp01(darkAccentHsl.l + 0.08),
	});

	return {
		seed,
		accent,
		darkAccent,
		adjusted: accent.toUpperCase() !== seed.toUpperCase(),
		shared: {
			// index.css defines these once, outside any `.dark` block: they are a
			// self-contained light chip (tint background + deep text) that reads
			// fine on a dark page, exactly as the house coral chip does today.
			"--color-coral-light": tint,
			// `--color-coral-dark` exists to be read *on* `--color-coral-light`
			// (the Draft-chip pairing), so it takes the contrast-checked value
			// rather than the raw deep step.
			"--color-coral-dark": onTint,
		},
		light: {
			"--color-coral": accent,
			"--color-coral-hover": hover,
			"--primary": shadcnTriple(accent),
			"--ring": shadcnTriple(accent),
			"--accent": shadcnTriple(tint),
			"--accent-foreground": shadcnTriple(onTint),
			"--sidebar-primary": shadcnTriple(accent),
			"--sidebar-ring": shadcnTriple(accent),
			"--sidebar-accent": shadcnTriple(tint),
			"--sidebar-accent-foreground": shadcnTriple(onTint),
		},
		dark: {
			// `--color-coral` has no dark variant in index.css — the house coral
			// happens to read acceptably on both surfaces, so nothing ever needed
			// one. A brand seed has no such luck: navy `#003388` on the dark card
			// is 1.2:1, i.e. invisible. These are the tokens painted directly
			// against the page surface, so they get the dark-corrected pair.
			"--color-coral": darkAccent,
			"--color-coral-hover": darkHover,
			"--primary": shadcnTriple(darkAccent),
			"--ring": shadcnTriple(darkAccent),
			"--sidebar-primary": shadcnTriple(darkAccent),
			"--sidebar-ring": shadcnTriple(darkAccent),
			// `--accent` / `--sidebar-accent` are deliberately absent. In dark
			// mode index.css makes them a neutral slate (`185 10% 15%`), not a
			// coral tint, so the house value is the right answer — and because
			// `light` is emitted under `:root:not(.dark)`, that house value is
			// reachable rather than being shadowed by the light tint.
		},
	};
}

/** Derive a theme straight from a brand bible's `theme_config.colors`. */
export function brandThemeFromColors(
	colors: Record<string, unknown> | null | undefined,
): BrandTheme | null {
	const seed = pickBrandSeed(colors);
	return seed ? deriveBrandTheme(seed) : null;
}
